import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  FormControlLabel,
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import Switch from '@mui/material/Switch';
import PersonIcon from '@mui/icons-material/Person';
import FolderIcon from '@mui/icons-material/Folder';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionSuggestField from './DescriptionSuggestField';
import CloseIcon from '@mui/icons-material/Close';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { utcToDisplayTimezone, displayTimezoneToUtc } from '../../utils/timezoneUtils';
import { API_BASE_URL, addMemberEmailToData } from '../../config/api';

import { useTeamspace, useOptionsForTeamspace } from '../../context/TeamspaceProvider';
import { useAuth } from '../../context/AuthContext';
import { isRestrictedStaff, canEditTimeEntryRate } from '../../config/staffRestrictions'; // Import useAuth

import axios from 'axios';
import { apiErrorMessage } from '../../utils/apiError';
import { useTheme } from '@mui/material/styles';
import { buildTagDescription, buildTagSuggestions, syncDescriptionWithTags } from '../../utils/tagDescription';
import ConcurrentEditNotice from '../Presence/ConcurrentEditNotice';
import useIsPhone from '../../hooks/useIsPhone';

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// These functions are now in TeamspaceProvider.js
// const fetchMembersForAutocomplete = async (teamspaceId) => { ... };
// const fetchProjectsForAutocomplete = async (teamspaceId) => { ... };
// const fetchTagsForAutocomplete = async (teamspaceId) => { ... };

// Helper functions to convert between minutes and HH:MM format
const minutesToHHMM = (minutes) => {
  if (!minutes || minutes === 0) return '00:00';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const HHMMToMinutes = (hhmm) => {
  if (!hhmm || hhmm === '') return 0;
  // Handle different input formats
  const parts = hhmm.split(':');
  if (parts.length === 2) {
    const hours = parseInt(parts[0]) || 0;
    const minutes = parseInt(parts[1]) || 0;
    return hours * 60 + minutes;
  }
  // Fallback: treat as minutes if no colon
  return parseInt(hhmm) || 0;
};

// Helper function to extract hours from HH:MM format
const getHoursFromHHMM = (hhmm) => {
  if (!hhmm || hhmm === '') return 0;
  const parts = hhmm.split(':');
  if (parts.length === 2) {
    return parseInt(parts[0]) || 0;
  }
  return 0;
};

const putTimeEntry = async (timeEntryData, teamspaceId, timeEntryId, user) => {
  timeEntryData.teamspace = teamspaceId
  // Add member_email to the request data
  const dataWithMemberEmail = addMemberEmailToData(timeEntryData, user);
  console.log('Sending time entry data to API:', dataWithMemberEmail);
  const apiUrl = `${API_BASE_URL}teamspaces/${teamspaceId}/time_entries/${timeEntryId}/`; 
  try {
    const response = await axios.put(apiUrl, dataWithMemberEmail);

    if (response.status !== 200) { 
      const errorData = response.data;
      const error = new Error(`API error: ${JSON.stringify(errorData)}`);
      // Preserve response for error handling
      error.response = response;
      throw error;
    }

    return response.data;
  } catch (error) {
    console.error("Failed to update time entry data:", error);
    // Preserve original axios error response if it exists
    if (error.response) {
      throw error;
    }
    // If it's our custom error, make sure response is preserved
    throw error;
  }
};

const EditTimeEntryForm = ({ open, handleClose, onTimeEntryUpdated, onTimeEntryAdded, onTimeEntryDeleted, initialData, groupEntries: groupEntriesProp = null }) => {

  const { selectedTeamspaceId, projectLookup, memberLookup, tagLookup, lookupLoading, teamspaceIdToNameMap, currentTeamspaceTimezone, projects, refreshTimeEntries, members, userPreferences, displayTimezone, startTimeEntryMutation, endTimeEntryMutation } = useTeamspace();
  const { user, role_dict } = useAuth(); // Get user and role_dict from AuthContext
  const theme = useTheme();
  const isPhone = useIsPhone();

  // Load options for the entry's own teamspace (may differ from the header's selected teamspace).
  // This ensures projects, tags, categories, and members all come from the correct teamspace
  // even when the user is viewing a cross-teamspace entry (e.g. JP entry while PH is selected).
  const entryTeamspaceId = initialData?.teamspace || selectedTeamspaceId;
  const entryFormOptions = useOptionsForTeamspace(entryTeamspaceId);
  // Also keep the header teamspace options available (e.g. for fallback lookups)
  const formOptions = useOptionsForTeamspace(selectedTeamspaceId);

  // All form dropdowns use the entry's teamspace options
  const formCategories = entryFormOptions.categories || [];

  // Combined member lookup: current teamspace + entry's teamspace.
  // Used everywhere member names are displayed so cross-teamspace members resolve to real names.
  const combinedMemberLookup = useMemo(
    () => ({ ...memberLookup, ...entryFormOptions.memberLookup }),
    [memberLookup, entryFormOptions.memberLookup]
  );

  // Determine if the current user is 'staff' in the selected teamspace
  const teamspaceName = teamspaceIdToNameMap[selectedTeamspaceId];
  const isStaff = user && teamspaceName && role_dict[teamspaceName]?.role?.includes('Staff');
  const canEditRate = canEditTimeEntryRate(user, role_dict, teamspaceName);
  // Kept as text while typing; sent only when it differs from the entry's rate, so
  // an ordinary edit never pins a rate the server would otherwise have reset.
  const [rateInput, setRateInput] = useState('');
  const initialRateText = initialData?.rate === null || initialData?.rate === undefined
    ? '' : String(initialData.rate);
  const rateChanged = canEditRate && rateInput.trim() !== initialRateText;
  useEffect(() => { setRateInput(initialRateText); }, [initialRateText, initialData?.id]);
  // DCC/PH Staff may log time for other members too (2026-10-08) — only the
  // entries they can see stay limited to their own.
  const canCreateForOthers = !!(user && teamspaceName && role_dict[teamspaceName]?.role?.some(r => ['Admin', 'Developer', 'Lead'].includes(r)))
    || isRestrictedStaff(user, role_dict, teamspaceName);
  
  // Find current user's member ID for staff users
  const currentUserMember = useMemo(() => {
    if (!user || !members.length) return null;
    return members.find(member => 
      member.email === user.email || 
      member.username === user.username ||
      member.label === user.username ||
      member.label === user.email
    );
  }, [user, members]); 
  
  // displayTimezone is now from context - always uses header's selectedTeamspaceId
  
  // Helper functions are now imported from utils/timezoneUtils.js
  // Wrapper functions that use displayTimezone from context for convenience
  const utcToDisplayTimezoneLocal = (utcDateString) => utcToDisplayTimezone(utcDateString, displayTimezone);
  const displayTimezoneToUtcLocal = (dayjsObj) => displayTimezoneToUtc(dayjsObj, displayTimezone);

  // Helper to round time to nearest 5 minutes (now optional, can be removed)
  const roundToNearestFiveMinutes = (time) => {
    return time.second(0);
  };

  const [error, setError] = useState(null);
  // Calculate initial end_time - use existing end_time if available, otherwise calculate from start_time + duration
  const getInitialEndTime = () => {
    if (initialData?.end_time) {
      return utcToDisplayTimezone(initialData.end_time);
    } else if (initialData?.start_time && initialData?.duration) {
      const startTime = utcToDisplayTimezone(initialData.start_time);
      return startTime.add(Math.round(initialData.duration / 60), 'minute');
    } else if (initialData?.start_time) {
        const startTime = utcToDisplayTimezoneLocal(initialData.start_time);
      return startTime.add(15, 'minute'); // Default 15 minutes if no duration
    }
    const now = (!displayTimezone || displayTimezone === 'UTC') 
      ? dayjs().utc() 
      : dayjs().tz(displayTimezone);
    const startTime = roundToNearestFiveMinutes(now);
    return now.hour(startTime.hour()).minute(startTime.minute()).second(0).add(15, 'minute');
  };

  // Get default_billable from user preferences, default to true if not set
  // Use existing billable value if available, otherwise use default_billable preference
  const defaultBillable = userPreferences?.default_billable !== undefined ? userPreferences.default_billable : true;
  
  const [formData, setFormData] = useState({
    description: initialData?.description || '',
    member: initialData?.member ? [String(initialData.member)] : [],
    project: initialData?.project || '',
    category: initialData?.category || '',
    tag: initialData?.tag !== null && initialData?.tag !== undefined 
      ? (Array.isArray(initialData.tag) ? initialData.tag : [initialData.tag])
      : [],
    billable: initialData?.billable !== undefined ? initialData.billable : defaultBillable,
    date: initialData?.start_time ? utcToDisplayTimezoneLocal(initialData.start_time) : ((!displayTimezone || displayTimezone === 'UTC') ? dayjs().utc() : dayjs().tz(displayTimezone)),
    start_time: initialData?.start_time ? utcToDisplayTimezoneLocal(initialData.start_time) : ((!displayTimezone || displayTimezone === 'UTC') ? dayjs().utc() : dayjs().tz(displayTimezone)),
    end_time: getInitialEndTime(),
    duration_minutes: initialData?.duration ? Math.round(initialData.duration / 60) : 15,
    notes: initialData?.notes || '',
    teamspace: initialData?.teamspace || null
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [durationInputValue, setDurationInputValue] = useState(null); // Track raw input value
  const [durationError, setDurationError] = useState(''); // Track duration validation error
  const durationInputRef = useRef(null); // Ref for duration input to preserve cursor position
  const durationPrevValueRef = useRef(null); // Track previous value for cursor calculation
  const memberEntriesRef = useRef([]);
  const [descriptionSuggestions, setDescriptionSuggestions] = useState([]);
  const [apiErrorDialogOpen, setApiErrorDialogOpen] = useState(false);
  const [duplicateCheckDialogOpen, setDuplicateCheckDialogOpen] = useState(false);
  const [duplicateCheckInfo, setDuplicateCheckInfo] = useState(null);
  const groupEntries = groupEntriesProp || [];
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [groupConfirmMode, setGroupConfirmMode] = useState(null); // 'myself' | 'all' | null
  const [billableSetByProject, setBillableSetByProject] = useState(false);
  const [billableOverriddenFromProject, setBillableOverriddenFromProject] = useState(false);
  const [expandedFields, setExpandedFields] = useState({
    member: false,
    project: false,
    tag: false,
  });
  
  const isGroupEntry = groupEntries.length > 1;

  const toggleField = (field) => {
    setExpandedFields(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const getSelectedLabel = (field, options, value) => {
    if (field === 'member') {
      if (!value || (Array.isArray(value) && value.length === 0)) return null;
      const memberIds = Array.isArray(value) ? value : [value];
      if (memberIds.length === 1) {
        const option = options.find(o => String(o.id) === String(memberIds[0]));
        return option ? option.label : null;
      }
      if (initialData?.member) {
        const primaryOption = options.find(o => String(o.id) === String(initialData.member));
        const primaryName = primaryOption ? primaryOption.label : `Member #${initialData.member}`;
        const othersCount = memberIds.length - 1;
        return `${primaryName}, and ${othersCount} other${othersCount !== 1 ? 's' : ''}`;
      }
      return `(${memberIds.length}) members`;
    }
    if (field === 'tag' || field === 'tags') {
      // Handle array of tag IDs
      if (!value || (Array.isArray(value) && value.length === 0)) return null;
      const tagIds = Array.isArray(value) ? value : [value];
      if (tagIds.length === 0) return null;
      if (tagIds.length === 1) {
        const option = options.find(o => o.id === parseInt(tagIds[0], 10));
        return option ? option.label : null;
      }
      return `(${tagIds.length} tags)`;
    }
    if (field === 'project') {
      // Handle project or category selection
      if (!value && !formData.category) return null;
      // Check category first
      if (formData.category) {
        const categoryOption = formCategories.find(c => c.id === formData.category);
        if (categoryOption) return categoryOption.label;
      }
      // Then check project
      if (value) {
        const option = options.find(o => o.id === value);
        return option ? option.label : null;
      }
      return null;
    }
    if (!value && value !== 0) return null;
    const option = options.find(o => o.id === value);
    return option ? option.label : null;
  };
  
  useEffect(() => {
    if (initialData) {
      // Calculate end_time - use existing end_time if available, otherwise calculate from start_time + duration
      let calculatedEndTime = null;
      if (initialData.end_time) {
        calculatedEndTime = utcToDisplayTimezoneLocal(initialData.end_time);
      } else if (initialData.start_time) {
        const startTime = utcToDisplayTimezoneLocal(initialData.start_time);
        const durationMinutes = initialData.duration ? Math.round(initialData.duration / 60) : 15;
        calculatedEndTime = startTime.add(durationMinutes, 'minute');
      }
      
      // For staff users editing, ensure member is set to current user if not already set
      const staffMemberId = isStaff && !initialData.member && currentUserMember ? String(currentUserMember.id) : null;
      const memberValue = staffMemberId ? [staffMemberId] : (initialData.member ? [String(initialData.member)] : []);

      setFormData({
        description: initialData.description || '',
        member: memberValue,
        project: initialData.project || '',
        category: initialData.category || '',
        tag: initialData.tag !== null && initialData.tag !== undefined 
          ? (Array.isArray(initialData.tag) ? initialData.tag : [initialData.tag])
          : [],
        billable: initialData.billable ?? true,
        date: initialData.start_time ? utcToDisplayTimezoneLocal(initialData.start_time) : ((!displayTimezone || displayTimezone === 'UTC') ? dayjs().utc() : dayjs().tz(displayTimezone)),
        start_time: initialData.start_time ? utcToDisplayTimezoneLocal(initialData.start_time) : ((!displayTimezone || displayTimezone === 'UTC') ? dayjs().utc() : dayjs().tz(displayTimezone)),
        end_time: calculatedEndTime,
        duration_minutes: initialData.duration ? Math.round(initialData.duration / 60) : 15,
        notes: initialData.notes || '',
        teamspace: initialData.teamspace || null
      });
      setValidationErrors({});
      setError(null);
      setDurationInputValue(null); // Reset duration input value
      setDurationError(''); // Reset duration error
      durationPrevValueRef.current = null; // Reset previous value ref
      // For staff users, automatically expand member field to show it's filled and locked
      if (isStaff && memberValue.length > 0) {
        setExpandedFields(prev => ({ ...prev, member: false }));
      }

      // Set billableSetByProject if entry has a project and its billable matches the project's billable
      if (initialData.project && projects.length > 0) {
        const matchingProject = projects.find(p => String(p.id) === String(initialData.project));
        if (matchingProject && initialData.billable === matchingProject.billable) {
          setBillableSetByProject(true);
        } else {
          setBillableSetByProject(false);
        }
      } else {
        setBillableSetByProject(false);
      }
      setBillableOverriddenFromProject(false);
    }
  }, [initialData, isStaff, currentUserMember, projects]);

  // Sync formData.member when group membership changes (provided by parent via prop)
  useEffect(() => {
    if (groupEntries.length > 1) {
      setFormData(prev => ({ ...prev, member: groupEntries.map(e => String(e.member)) }));
    }
  }, [groupEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tag names offered alongside the recently-used descriptions, selected tags first
  const tagSuggestions = useMemo(
    () => buildTagSuggestions({ selectedTagIds: formData.tag, tagLookup: tagLookup }),
    [formData.tag, tagLookup]
  );

  // Helper: compute suggestions from raw entries based on project/category
  const computeDescriptionSuggestions = (entries, projectId, categoryId, memberId) => {
    const memberEntries = entries.filter(e => String(e.member) === String(memberId));
    let filtered = memberEntries;
    if (projectId || categoryId) {
      const projectFiltered = memberEntries.filter(e =>
        projectId ? String(e.project) === String(projectId) : String(e.category) === String(categoryId)
      );
      if (projectFiltered.length > 0) filtered = projectFiltered;
    }
    const seen = new Set();
    const result = [];
    for (const entry of filtered) {
      const desc = entry.description?.trim();
      if (desc && !seen.has(desc)) { seen.add(desc); result.push(desc); }
    }
    return result.slice(0, 20);
  };

  // Fetch recent entries for description suggestions when form opens or teamspace/member changes
  useEffect(() => {
    memberEntriesRef.current = [];
    setDescriptionSuggestions([]);
    if (!open || !selectedTeamspaceId || !currentUserMember?.id) return;
    const fetchDescriptions = async () => {
      try {
        const params = new URLSearchParams({ page_size: 100, member: currentUserMember.id, is_optimized: 'true' });
        const response = await axios.get(
          `${API_BASE_URL}teamspaces/${selectedTeamspaceId}/time_entries/?${params.toString()}`
        );
        const entries = Array.isArray(response.data) ? response.data : (response.data.results || []);
        memberEntriesRef.current = entries;
        setDescriptionSuggestions(
          computeDescriptionSuggestions(entries, formData.project, formData.category, currentUserMember.id)
        );
      } catch (err) {
        console.warn('Failed to fetch description suggestions:', err);
      }
    };
    fetchDescriptions();
  }, [open, selectedTeamspaceId, currentUserMember]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recompute suggestions when project/category selection changes
  useEffect(() => {
    if (!currentUserMember?.id || !memberEntriesRef.current.length) return;
    setDescriptionSuggestions(
      computeDescriptionSuggestions(memberEntriesRef.current, formData.project, formData.category, currentUserMember.id)
    );
  }, [formData.project, formData.category]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAutocompleteChange = (name) => (event, value) => {
    if (name === 'tag') {
      // Handle multiple tags
      const tagIds = Array.isArray(value) ? value.map(v => v.id) : (value ? [value.id] : []);
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: tagIds,
        // Keep a tag-derived description in sync with the new tags; a
        // description the user typed themselves is left untouched.
        description: syncDescriptionWithTags({
          description: prevFormData.description,
          previousTagIds: prevFormData.tag,
          nextTagIds: tagIds,
          tagLookup,
        }),
      }));
    } else if (name === 'project') {
      // Handle project/category selection - mutually exclusive
      if (value) {
        const defaultBillable = userPreferences?.default_billable !== undefined ? userPreferences.default_billable : true;
        if (value.isCategory) {
          // Selected a category - set category and clear project; category entries are never billable
          setFormData(prevFormData => ({
            ...prevFormData,
            category: value.id,
            project: '',
            billable: false,
          }));
          setBillableSetByProject(false);
          setBillableOverriddenFromProject(false);
        } else {
          // Selected a project - set project, clear category, apply project's billable
          setFormData(prevFormData => ({
            ...prevFormData,
            project: value.id,
            category: '',
            billable: value.billable !== undefined ? value.billable : defaultBillable,
          }));
          setBillableSetByProject(true);
          setBillableOverriddenFromProject(false);
        }
      } else {
        const defaultBillable = userPreferences?.default_billable !== undefined ? userPreferences.default_billable : true;
        // Cleared selection - clear both, revert billable to default
        setFormData(prevFormData => ({
          ...prevFormData,
          project: '',
          category: '',
          billable: defaultBillable,
        }));
        setBillableSetByProject(false);
        setBillableOverriddenFromProject(false);
      }
    } else if (name === 'member') {
      const memberIds = Array.isArray(value) ? value.map(v => String(v.id)) : (value ? [String(value.id)] : []);
      setFormData(prevFormData => ({
        ...prevFormData,
        member: memberIds,
      }));
    } else {
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: value ? value.id : ''
      }));
    }
  };

  const handleDateTimeChange = (name) => (newValue) => {
    if (name === 'start_time') {
      setFormData(prevFormData => {
        const updated = { ...prevFormData, start_time: newValue };
        // If end_time exists, recalculate duration
        if (updated.end_time && updated.end_time.isValid() && newValue && newValue.isValid()) {
          const startDateTime = updated.date
            .hour(newValue.hour())
            .minute(newValue.minute())
            .second(0);
          let endDateTime = updated.date
            .hour(updated.end_time.hour())
            .minute(updated.end_time.minute())
            .second(0);
          let diffMinutes = endDateTime.diff(startDateTime, 'minute');
          // Handle case where end_time is before start_time (next day)
          if (diffMinutes < 0) {
            endDateTime = endDateTime.add(1, 'day');
            diffMinutes = endDateTime.diff(startDateTime, 'minute');
          }
          updated.duration_minutes = Math.max(diffMinutes, 0);
        } else if (!updated.end_time || !updated.end_time.isValid()) {
          // If end_time doesn't exist, set duration to 0
          updated.duration_minutes = 0;
        }
        return updated;
      });
    } else if (name === 'end_time') {
      setFormData(prevFormData => {
        const updated = { ...prevFormData, end_time: newValue };
        // If end_time is set and start_time exists, calculate duration
        if (newValue && newValue.isValid() && updated.start_time && updated.start_time.isValid()) {
          const startDateTime = updated.date
            .hour(updated.start_time.hour())
            .minute(updated.start_time.minute())
            .second(0);
          let endDateTime = updated.date
            .hour(newValue.hour())
            .minute(newValue.minute())
            .second(0);
          let diffMinutes = endDateTime.diff(startDateTime, 'minute');
          if (diffMinutes < 0) {
            endDateTime = endDateTime.add(1, 'day');
            diffMinutes = endDateTime.diff(startDateTime, 'minute');
          }
          updated.duration_minutes = Math.max(diffMinutes, 0);
        } else if (!newValue) {
          // If end_time is cleared, don't change duration
        }
        return updated;
      });
    } else {
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: newValue
      }));
    }
  };

  // Handle duration change - update end_time
  const handleDurationChange = (inputValue) => {
    // If empty, allow it during typing but don't update formData yet
    if (inputValue === '' || inputValue.trim() === '') {
      setDurationInputValue('');
      return;
    }
    // HHMMToMinutes already handles minutes > 59 correctly (e.g., 01:80 = 140 minutes)
    const minutes = HHMMToMinutes(inputValue);
    const clampedValue = Math.min(Math.max(minutes, 0), 1440); // 24 hours max
    setDurationInputValue(null); // Clear raw input, use normalized value
    
    setFormData(prev => {
      const updated = { ...prev, duration_minutes: clampedValue };
      // If start_time exists, calculate end_time
      if (updated.start_time && updated.start_time.isValid() && clampedValue > 0) {
        const startDateTime = updated.date
          .hour(updated.start_time.hour())
          .minute(updated.start_time.minute())
          .second(0);
        updated.end_time = startDateTime.add(clampedValue, 'minute');
      } else {
        updated.end_time = null;
      }
      return updated;
    });
  };

  const validateForm = () => {
    console.log("Validating form...", formData);
    const errors = {};
    // Description is optional
    if ((canCreateForOthers || !isStaff) && formData.member.length === 0) {
      errors.member = 'Member is required.';
    }
    // Project is required in the UI even though time_entry.project is nullable.
    // The field is a single combined picker, so a time entry category satisfies it too.
    if (!formData.project && !formData.category) {
      errors.project = 'Project is required.';
    }
    if (rateChanged && !/^\d+$/.test(rateInput.trim())) {
      errors.rate = 'Rate must be a whole number of 0 or more.';
    }
    
    // Validate date
    if (!formData.date || !formData.date.isValid()) {
      errors.date = 'Date is required and must be valid.';
    }
    // Validate start time
    if (!formData.start_time || !formData.start_time.isValid()) {
      errors.start_time = 'Start time is required and must be valid.';
    }
    // Validate duration - optional, but if provided must be valid
    // Also check if either duration or end_time is provided
    const hasDuration = formData.duration_minutes && formData.duration_minutes > 0;
    const hasEndTime = formData.end_time && formData.end_time.isValid();
    
    if (!hasDuration && !hasEndTime) {
      // Both are optional, so no error
    } else if (formData.duration_minutes && formData.duration_minutes < 0) {
      errors.duration_minutes = 'Duration cannot be negative.';
    }
    setValidationErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    console.log("Validation errors:", errors);
    console.log("Form is valid:", isValid);
    return isValid;
  };
  
  const checkForDuplicates = async (memberId, startDateUTC, endDateUTC, basePayload) => {
    const params = new URLSearchParams({ start_date: startDateUTC, end_date: startDateUTC, timezone: 'UTC', page_size: 200 });
    const response = await axios.get(`${API_BASE_URL}teamspaces/${selectedTeamspaceId}/time_entries/?${params.toString()}`);
    const entries = Array.isArray(response.data) ? response.data : (response.data.results || []);
    const targetEndMs = endDateUTC ? new Date(endDateUTC).getTime() : null;
    return entries.some(entry => {
      if (String(entry.id) === String(initialData.id)) return false; // exclude self
      if (String(entry.member) !== String(memberId)) return false;
      const entryEndMs = entry.end_time ? new Date(entry.end_time).getTime() : null;
      if (entryEndMs !== targetEndMs) return false;
      if (basePayload.project) { if (String(entry.project) !== String(basePayload.project)) return false; } else if (entry.project) return false;
      if (basePayload.category) { if (String(entry.category) !== String(basePayload.category)) return false; } else if (entry.category) return false;
      if ((entry.description || '') !== (basePayload.description || '')) return false;
      return true;
    });
  };

  const doEdit = async (basePayload, scope = 'myself') => {
    startTimeEntryMutation();
    const entryTeamspaceId = initialData?.teamspace || selectedTeamspaceId;
    try {
      // This entry only: other members in a group keep their own rates, and an
      // entry created for an added member takes the default.
      const rateOverride = rateChanged ? { rate_override: parseInt(rateInput.trim(), 10) } : {};
      const primaryMemberId = String(initialData.member || '') || (isStaff && currentUserMember ? String(currentUserMember.id) : '');
      const existingMemberIds = isGroupEntry ? groupEntries.map(e => String(e.member)) : [];
      const currentMemberIds = isGroupEntry ? formData.member.map(String) : [];
      const addedIds = isGroupEntry ? currentMemberIds.filter(id => !existingMemberIds.includes(id)) : [];
      const removedIds = isGroupEntry ? existingMemberIds.filter(id => !currentMemberIds.includes(id)) : [];
      // For non-group edits: any selected member beyond the primary gets a new entry
      const nonGroupExtraIds = !isGroupEntry
        ? formData.member.map(String).filter(id => id !== primaryMemberId)
        : [];

      if (scope === 'all') {
        for (const groupEntry of groupEntries) {
          if (removedIds.includes(String(groupEntry.member))) continue; // will be deleted
          const payload = {
            ...basePayload,
            member: String(groupEntry.member),
            ...(String(groupEntry.id) === String(initialData.id) ? rateOverride : {}),
          };
          const result = await putTimeEntry(payload, entryTeamspaceId, groupEntry.id, user);
          const updatedEntry = result.time_entry || result;
          if (onTimeEntryUpdated) onTimeEntryUpdated(updatedEntry);
        }
      } else {
        const payload = { ...basePayload, member: primaryMemberId, ...rateOverride };
        const result = await putTimeEntry(payload, entryTeamspaceId, initialData.id, user);
        console.log('Time Entry successfully updated:', result);
        setValidationErrors({});
        const updatedEntry = result.time_entry || result;
        if (onTimeEntryUpdated) onTimeEntryUpdated(updatedEntry);

        // For non-group edits: create new entries for any additional selected members
        for (const memberId of nonGroupExtraIds) {
          const extraPayload = addMemberEmailToData({ ...basePayload, member: memberId, teamspace: entryTeamspaceId }, user);
          const response = await axios.post(`${API_BASE_URL}teamspaces/${entryTeamspaceId}/time_entries/`, extraPayload);
          if (onTimeEntryAdded) onTimeEntryAdded(response.data);
        }
      }

      // Create new entries for added members (group edits).
      // For 'myself' scope, use the group's existing data so the new entry matches the rest of the group.
      // For 'all' scope, use basePayload (the updated data being applied to everyone).
      const referenceEntry = scope === 'myself'
        ? (groupEntries.find(e => String(e.id) !== String(initialData.id)) || groupEntries[0])
        : null;
      const addedMemberBasePayload = (scope === 'myself' && referenceEntry)
        ? {
            description: referenceEntry.description || '',
            billable: Boolean(referenceEntry.billable),
            start_time: referenceEntry.start_time,
            end_time: referenceEntry.end_time,
            notes: referenceEntry.notes || '',
            project: referenceEntry.project || null,
            category: referenceEntry.category || null,
            tag: Array.isArray(referenceEntry.tag) ? referenceEntry.tag : [],
          }
        : basePayload;
      for (const memberId of addedIds) {
        const payload = addMemberEmailToData({ ...addedMemberBasePayload, member: memberId, teamspace: entryTeamspaceId }, user);
        const response = await axios.post(`${API_BASE_URL}teamspaces/${entryTeamspaceId}/time_entries/`, payload);
        if (onTimeEntryAdded) onTimeEntryAdded(response.data);
      }

      // Delete entries for removed members (never delete the entry being directly edited)
      for (const memberId of removedIds) {
        const entryToDelete = groupEntries.find(e => String(e.member) === memberId);
        if (entryToDelete && String(entryToDelete.id) !== String(initialData.id)) {
          const deleteUrl = currentUserMember?.id
            ? `${API_BASE_URL}teamspaces/${entryTeamspaceId}/time_entries/${entryToDelete.id}/?action_by=${currentUserMember.id}`
            : `${API_BASE_URL}teamspaces/${entryTeamspaceId}/time_entries/${entryToDelete.id}/`;
          await axios.delete(deleteUrl);
          if (onTimeEntryDeleted) onTimeEntryDeleted(entryToDelete.id);
        }
      }

      if (refreshTimeEntries) refreshTimeEntries();
      handleClose();
    } catch (err) {
      console.error('Error updating time entry:', err);
      const isNetworkError = !err.response && (
        err.message?.includes('Network Error') ||
        err.code === 'ERR_CONNECTION_REFUSED' ||
        err.code === 'ERR_NETWORK' ||
        err.message?.includes('ERR_CONNECTION_REFUSED')
      );
      const responseStatus = err.response?.status;
      if (isNetworkError || (responseStatus && responseStatus !== 200)) {
        setApiErrorDialogOpen(true);
        return;
      }
      // err.message on an axios failure is "Request failed with status code 400" --
      // technically an error message, practically useless to whoever has to fix the
      // form. The API's own reason is one level down, in the response body.
      setError(apiErrorMessage(err, 'Failed to save the time entry.'));
    } finally {
      endTimeEntryMutation();
    }
  };

  const handleDeleteConfirm = async (scope = 'single') => {
    if (!initialData) return;
    const entriesToDelete = scope === 'all' && isGroupEntry
      ? groupEntries
      : [initialData];

    setDeleteDialogOpen(false);
    setDeleting(true);
    try {
      startTimeEntryMutation();
      for (const entry of entriesToDelete) {
        const tsId = entry.teamspace ?? entry.teamspace_id ?? selectedTeamspaceId;
        const deleteUrl = currentUserMember?.id
          ? `${API_BASE_URL}teamspaces/${tsId}/time_entries/${entry.id}/?action_by=${currentUserMember.id}`
          : `${API_BASE_URL}teamspaces/${tsId}/time_entries/${entry.id}/`;
        await axios.delete(deleteUrl);
        if (onTimeEntryDeleted) onTimeEntryDeleted(entry.id);
      }
      handleClose();
    } catch (err) {
      console.error('Error deleting time entry:', err);
      setApiErrorDialogOpen(true);
    } finally {
      setDeleting(false);
      endTimeEntryMutation();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    console.log("Submit button clicked. Attempting to validate form...");
    if (validateForm()) {
      await performSubmit();
    }
  };

  const performSubmit = async (scope = 'myself') => {
    console.log("Form is valid. Attempting to submit...");
    // Combine date with times to create full datetime objects
    const dateTimeStr = `${formData.date.format('YYYY-MM-DD')}T${formData.start_time.format('HH:mm:ss')}`;
    const startDateTime = (!displayTimezone || displayTimezone === 'UTC')
      ? dayjs.utc(dateTimeStr)
      : dayjs.tz(dateTimeStr, displayTimezone);

    const startDateUTC = displayTimezoneToUtcLocal(startDateTime);

    let endDateUTC;
    if (formData.end_time && formData.end_time.isValid()) {
      const endDateTimeStr = `${formData.date.format('YYYY-MM-DD')}T${formData.end_time.format('HH:mm:ss')}`;
      let endDateTime = (!displayTimezone || displayTimezone === 'UTC')
        ? dayjs.utc(endDateTimeStr)
        : dayjs.tz(endDateTimeStr, displayTimezone);
      if (endDateTime.isBefore(startDateTime)) {
        endDateTime = endDateTime.add(1, 'day');
      }
      endDateUTC = displayTimezoneToUtcLocal(endDateTime);
    } else if (formData.duration_minutes && formData.duration_minutes > 0) {
      const durationSeconds = formData.duration_minutes * 60;
      const startDate = new Date(startDateUTC);
      endDateUTC = new Date(startDate.getTime() + (durationSeconds * 1000)).toISOString();
    } else {
      throw new Error('Either duration or end time must be provided.');
    }

    const memberId = String(initialData.member || '') || (!canCreateForOthers && isStaff && currentUserMember ? String(currentUserMember.id) : '');

    const trimmedDescription = formData.description?.trim();
    let description = formData.description || '';
    if (!trimmedDescription) {
      // Use all selected tag names as description if description is empty
      description = buildTagDescription(formData.tag, tagLookup) || description;
    }

    const projectExists = formData.project && projectAndCategoryOptions.some(o => String(o.id) === String(formData.project) && !o.isCategory);
    const categoryExists = formData.category && projectAndCategoryOptions.some(o => String(o.id) === String(formData.category) && o.isCategory);
    const validTagIds = formData.tag && Array.isArray(formData.tag)
      ? formData.tag.filter(id => tagOptions.some(o => String(o.id) === String(id)))
      : [];

    const basePayload = {
      description,
      billable: Boolean(formData.billable) && !formData.category,
      start_time: startDateUTC,
      end_time: endDateUTC,
      notes: formData.notes || '',
      project: projectExists ? parseInt(formData.project, 10) : null,
      category: categoryExists ? formData.category : null,
      tag: validTagIds.length > 0 ? validTagIds.map(id => parseInt(id, 10)) : [],
      lastedited_by: currentUserMember?.id,
    };
    if (projectExists) basePayload.category = null;
    if (categoryExists) basePayload.project = null;

    if (scope === 'myself') {
      try {
        const isDuplicate = await checkForDuplicates(memberId, startDateUTC, endDateUTC, basePayload);
        if (isDuplicate) {
          setDuplicateCheckInfo({ memberId, basePayload, scope });
          setDuplicateCheckDialogOpen(true);
          return;
        }
      } catch (err) {
        console.error('Duplicate check failed, proceeding anyway:', err);
      }
    }

    await doEdit(basePayload, scope);
  };

  // Categories come from cached options (useOptionsForTeamspace)

  // Convert lookup maps to array of options for Autocomplete components.
  // Use the entry's teamspace options so cross-teamspace entries show the right projects/tags.
  const availableProjects = useMemo(() => {
    const rawProjects = entryFormOptions.projects || [];
    const options = rawProjects
      .filter(p => !p.is_archived)
      .map(p => ({ id: p.id, label: p.full_project_name || p.name || '', isCategory: false, billable: p.billable }))
      .sort((a, b) => (a.label || '').toLowerCase().localeCompare((b.label || '').toLowerCase()));
    // Project is required, but archived projects are filtered out of the picker. If the entry being
    // edited already sits on one, inject it — otherwise `projectExists` below is false and the save
    // silently nulls a field the form just told the user was required. Same trick as the
    // "(inactive)" member injected into memberOptions.
    const entryProjectId = initialData?.project ? String(initialData.project) : null;
    if (entryProjectId && !options.some(o => String(o.id) === entryProjectId)) {
      const archivedProject = rawProjects.find(p => String(p.id) === entryProjectId);
      if (archivedProject) {
        options.push({
          id: archivedProject.id,
          label: `${archivedProject.full_project_name || archivedProject.name || ''} (archived)`,
          isCategory: false,
          billable: archivedProject.billable,
        });
      }
    }
    return options;
  }, [entryFormOptions.projects, initialData?.project]);
  
  // Combine categories (first) and projects for the project dropdown
  const projectAndCategoryOptions = useMemo(() => {
    // Categories first, then projects
    return [...formCategories, ...availableProjects];
  }, [formCategories, availableProjects]);
  
  // Use the entry's teamspace members for the member dropdown so cross-teamspace entries
  // show the correct member list (e.g. TH members when JP is the header teamspace).
  // If the entry belongs to an inactive member, inject that member with an "(inactive)" label.
  const memberOptions = useMemo(() => {
    const options = (entryFormOptions.members || []).map(m => ({ id: String(m.id), label: m.label }));
    const entryMemberId = initialData?.member ? String(initialData.member) : null;
    if (entryMemberId && !options.some(o => o.id === entryMemberId)) {
      const inactiveMember = (entryFormOptions.allMembers || []).find(m => String(m.id) === entryMemberId);
      if (inactiveMember) {
        options.push({ id: entryMemberId, label: `${inactiveMember.label} (inactive)` });
      }
    }
    return options;
  }, [entryFormOptions.members, entryFormOptions.allMembers, initialData?.member]);
  const tagOptions = useMemo(
    () => (entryFormOptions.tags || []).map(t => ({ id: t.id, label: t.label || t.name || '' })),
    [entryFormOptions.tags]
  );

  // Effect to set member when user is staff and memberOptions are loaded
  useEffect(() => {
    if (isStaff && user && memberOptions.length > 0 && !initialData) {
      const staffMember = memberOptions.find(member => member.label === user.id);
      if (staffMember && !formData.member.includes(String(staffMember.id))) {
        setFormData(prevFormData => ({
          ...prevFormData,
          member: [String(staffMember.id)],
        }));
      }
    }
  }, [isStaff, user, memberOptions, formData.member, initialData]);


  return (
    <>
    <Dialog open={open} onClose={handleClose} fullScreen={isPhone}>
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ color: theme.palette.text.primary, backgroundColor: theme.palette.background.default, display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          Edit Time Entry
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {initialData && (
              <IconButton
                size="small"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={deleting}
                title="Delete time entry"
                sx={{ color: theme.palette.error.main }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton size="small" onClick={handleClose} title="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
          <DialogContent sx={{ backgroundColor: theme.palette.background.default }}>
            {/* Soft warning: someone else has this entry's form open (Time Entries page). */}
            <ConcurrentEditNotice grid="time_entries" rowId={open ? initialData?.id : null} sx={{ mt: 1 }} />
            <DescriptionSuggestField
              autoFocus
              bulbOnly
              margin="dense"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              variant="standard"
              suggestions={descriptionSuggestions}
              tagSuggestions={tagSuggestions}
              onSelect={(desc) => setFormData(prev => ({ ...prev, description: desc }))}
              sx={{ p:1 }}
            />
            
            {/* Icon buttons arranged horizontally with wrapping */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
              {/* Member Field with Icon */}
            <>
              {formData.member.length === 0 && (
                <IconButton
                  onClick={() => toggleField('member')}
                  sx={{ color: theme.palette.text.primary }}
                >
                  <PersonIcon />
                </IconButton>
              )}
              {formData.member.length > 0 && (
                <Chip
                  icon={<PersonIcon sx={{ color: theme.palette.text.primary }} />}
                  label={getSelectedLabel('member', memberOptions, formData.member) || (currentUserMember?.label || user?.username || user?.email_alt)}
                  onClick={() => canCreateForOthers && toggleField('member')}
                  onDelete={canCreateForOthers ? () => {
                    setFormData(prev => ({ ...prev, member: [] }));
                    setExpandedFields(prev => ({ ...prev, member: false }));
                  } : undefined}
                  variant="outlined"
                  sx={{
                    cursor: canCreateForOthers ? 'pointer' : 'default',
                    borderColor: theme.palette.info.main + '4D',
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.info.main + '1A',
                    opacity: canCreateForOthers ? 1 : 0.7
                  }}
                />
              )}
              {validationErrors.member && (
                <Typography variant="caption" color="error">
                  {validationErrors.member}
                </Typography>
              )}
            </>

              {/* Project Field with Icon */}
              <>
                {!formData.project && !formData.category && (
                  <IconButton 
                    onClick={() => toggleField('project')}
                    sx={{ color: theme.palette.text.primary }}
                  >
                    <FolderIcon />
                  </IconButton>
                )}
                {(formData.project || formData.category) && (
                  <Chip 
                    icon={<FolderIcon sx={{ color: theme.palette.text.primary }} />}
                    label={getSelectedLabel('project', availableProjects, formData.project)}
                    onClick={() => toggleField('project')}
                    onDelete={() => {
                      setFormData(prev => ({ ...prev, project: '', category: '' }));
                      setExpandedFields(prev => ({ ...prev, project: false }));
                      setBillableSetByProject(false);
                      setBillableOverriddenFromProject(false);
                    }}
                    variant="outlined"
                    sx={{ cursor: 'pointer', borderColor: theme.palette.info.main + '4D', color: theme.palette.text.primary, backgroundColor: theme.palette.info.main + '1A' }}
                  />
                )}
                {validationErrors.project && (
                  <Typography variant="caption" color="error">
                    {validationErrors.project}
                  </Typography>
                )}
              </>

              {/* Tag Field with Icon */}
              <>
                {(!formData.tag || (Array.isArray(formData.tag) && formData.tag.length === 0)) && (
                  <IconButton 
                    onClick={() => toggleField('tag')}
                    sx={{ color: theme.palette.text.primary }}
                  >
                    <LocalOfferIcon />
                  </IconButton>
                )}
                {formData.tag && Array.isArray(formData.tag) && formData.tag.length > 0 && (
                  <Chip 
                    icon={<LocalOfferIcon sx={{ color: theme.palette.text.primary }} />}
                    label={getSelectedLabel('tag', tagOptions, formData.tag)}
                    onClick={() => toggleField('tag')}
                    onDelete={() => {
                      setFormData(prev => ({
                        ...prev,
                        tag: [],
                        description: syncDescriptionWithTags({
                          description: prev.description,
                          previousTagIds: prev.tag,
                          nextTagIds: [],
                          tagLookup,
                        }),
                      }));
                      setExpandedFields(prev => ({ ...prev, tag: false }));
                    }}
                    variant="outlined"
                    sx={{ cursor: 'pointer', borderColor: theme.palette.info.main + '4D', color: theme.palette.text.primary, backgroundColor: theme.palette.info.main + '1A' }}
                  />
                )}
                {validationErrors.tag && (
                  <Typography variant="caption" color="error">
                    {validationErrors.tag}
                  </Typography>
                )}
              </>

              {/* Billable Field with Icon */}
              <>
                <Tooltip title={formData.category ? 'Categories are non-billable' : ''}>
                  <span>
                    <IconButton
                      disabled={Boolean(formData.category)}
                      onClick={() => {
                        if (billableSetByProject) setBillableOverriddenFromProject(true);
                        setFormData(prev => ({ ...prev, billable: !prev.billable }));
                        setBillableSetByProject(false);
                      }}
                      sx={{
                        color: (formData.billable && !formData.category) ? '#FFD700' : theme.palette.text.primary,
                        ...(billableSetByProject && {
                          boxShadow: '0 0 0 2px #f44336',
                        }),
                      }}
                      size="small"
                    >
                      <Typography sx={{
                        fontSize: '1rem',
                        fontWeight: (formData.billable && !formData.category) ? 'bold' : 'normal',
                      }}>
                        $
                      </Typography>
                    </IconButton>
                  </span>
                </Tooltip>
                {billableOverriddenFromProject && (
                  <Typography variant="caption" sx={{ color: 'warning.main' }}>
                    Billable changed from project default
                  </Typography>
                )}
              </>
            </Box>

            {/* Expandable Autocomplete fields */}
            {expandedFields.member && (
              <Autocomplete
                multiple
                required
                disabled={!canCreateForOthers}
                disablePortal
                options={memberOptions}
                loading={lookupLoading}
                open
                onClose={(event, reason) => {
                  if (reason === 'blur' || reason === 'escape') {
                    setExpandedFields(prev => ({ ...prev, member: false }));
                  }
                }}
                value={memberOptions.filter(option => formData.member.includes(String(option.id)))}
                onChange={canCreateForOthers ? handleAutocompleteChange('member') : undefined}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                filterOptions={(options, state) =>
                  options.filter(option => {
                    const l = (option && option.label) ? String(option.label) : '';
                    const q = (state && state.inputValue) ? String(state.inputValue) : '';
                    return l.toLowerCase().includes(q.toLowerCase());
                  })
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.label}
                      size="small"
                    />
                  ))
                }
                sx={{ width: '100%', mb: 1 }}
                renderInput={(params) => <TextField {...params} label="Member" autoFocus disabled={!canCreateForOthers}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                    },
                    '& fieldset': {
                      borderColor: theme.palette.info.main + '4D',
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.info.main + '80',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.info.main,
                    },
                  }} />}
              />
            )}
            {expandedFields.project && (
            <Autocomplete
              disablePortal
              options={projectAndCategoryOptions}
              loading={lookupLoading}
                open
                onClose={() => setExpandedFields(prev => ({ ...prev, project: false }))}
              value={
                formData.category 
                  ? projectAndCategoryOptions.find(option => option.id === formData.category && option.isCategory) || null
                  : (formData.project 
                      ? projectAndCategoryOptions.find(option => option.id === formData.project && !option.isCategory) || null
                      : null)
              }
              onChange={handleAutocompleteChange('project')}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, state) =>
                  options.filter(option => {
                    const l = (option && option.label) ? String(option.label) : '';
                    const q = (state && state.inputValue) ? String(state.inputValue) : '';
                    return l.toLowerCase().includes(q.toLowerCase());
                  })
                }
                sx={{ width: '100%', mb: 1 }}
                renderInput={(params) => <TextField {...params} label="Project or Category" autoFocus />}
              />
            )}
            {expandedFields.tag && (
            <Autocomplete
              multiple
              disablePortal
              options={tagOptions}
              loading={lookupLoading}
              open={expandedFields.tag}
              onClose={(event, reason) => {
                // Only close on blur or escape, not on selection
                if (reason === 'blur' || reason === 'escape') {
                  setExpandedFields(prev => ({ ...prev, tag: false }));
                }
              }}
              value={Array.isArray(formData.tag) && formData.tag.length > 0
                ? tagOptions.filter(option => formData.tag.includes(option.id))
                : []}
              onChange={handleAutocompleteChange('tag')}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, state) => 
                  options.filter(option => 
                    option.label.toLowerCase().includes(state.inputValue.toLowerCase())
                  )
                }
                sx={{ width: '100%', mb: 1 }}
                renderInput={(params) => <TextField {...params} label="Tags (optional)" autoFocus />}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.label}
                      size="small"
                    />
                  ))
                }
              />
            )}
            
            {/* Date and Time Fields */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Date"
                  type="date"
                  value={formData.date ? formData.date.format('YYYY-MM-DD') : ''}
                  onChange={(e) => {
                    const newDate = dayjs(e.target.value);
                    if (newDate.isValid()) {
                      setFormData(prev => {
                        const updated = { ...prev, date: newDate };
                        // If end_time exists and start_time exists, update end_time to keep same duration
                        if (updated.end_time && updated.end_time.isValid() && updated.start_time && updated.start_time.isValid()) {
                          const oldStartDateTime = prev.date
                            .hour(prev.start_time.hour())
                            .minute(prev.start_time.minute())
                            .second(0);
                          const oldEndDateTime = prev.end_time;
                          const durationMinutes = oldEndDateTime.diff(oldStartDateTime, 'minute');
                          
                          const newStartDateTime = newDate
                            .hour(updated.start_time.hour())
                            .minute(updated.start_time.minute())
                            .second(0);
                          updated.end_time = newStartDateTime.add(durationMinutes, 'minute');
                        }
                        return updated;
                      });
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  margin="dense"
                  error={!!validationErrors.date}
                  helperText={validationErrors.date}
                  sx={{ flex: 2 }}
                />
                {/* Timezone Field - Beside date picker */}
                <TextField
                  label="Timezone"
                  value={displayTimezone || 'UTC'}
                  InputProps={{
                    readOnly: true,
                  }}
                  margin="dense"
                  variant="outlined"
                  sx={{ 
                    flex: 1,
                    '& .MuiInputBase-input': {
                      color: theme.palette.text.secondary,
                      cursor: 'default',
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.info.main + '40',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.info.main + '80',
                    },
                    '& .MuiOutlinedInput-root.Mui-disabled': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.info.main + '40',
                      },
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MobileTimePicker 
                    label="Start Time"
                    value={formData.start_time}
                    onChange={handleDateTimeChange('start_time')}
                    ampm
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: 'dense',
                        required: true,
                        error: !!validationErrors.start_time,
                        helperText: validationErrors.start_time,
                        inputProps: { 
                          inputMode: 'numeric',
                          pattern: '[0-9: ]*',
                          placeholder: 'HH:MM AM/PM'
                        },
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                          },
                          '& .MuiInputLabel-root': {
                            color: theme.palette.text.secondary,
                          },
                          '& fieldset': {
                            borderColor: theme.palette.info.main + '4D',
                          },
                          '&:hover fieldset': {
                            borderColor: theme.palette.info.main + '80',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.info.main,
                          },
                        }
                      },
                      actionBar: {
                        actions: ['cancel', 'accept'],
                        sx: {
                          '& .MuiButtonBase-root': {
                            color: theme.palette.text.primary,
                          }
                        }
                      }
                    }}
                    sx={{ flex: 1 }}
                  />
                  <MobileTimePicker 
                    label="End Time (optional)"
                    value={formData.end_time}
                    onChange={handleDateTimeChange('end_time')}
                    ampm
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: 'dense',
                        error: !!validationErrors.end_time,
                        inputProps: { 
                          inputMode: 'numeric',
                          pattern: '[0-9: ]*',
                          placeholder: 'HH:MM AM/PM'
                        },
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                          },
                          '& .MuiInputLabel-root': {
                            color: theme.palette.text.secondary,
                          },
                          '& fieldset': {
                            borderColor: theme.palette.info.main + '4D',
                          },
                          '&:hover fieldset': {
                            borderColor: theme.palette.info.main + '80',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.info.main,
                          },
                        }
                      },
                      actionBar: {
                        actions: ['cancel', 'accept'],
                        sx: {
                          '& .MuiButtonBase-root': {
                            color: theme.palette.text.primary,
                          }
                        }
                      }
                    }}
                    sx={{ flex: 1 }}
                  />
                </LocalizationProvider>
              </Box>
              {formData.start_time && formData.start_time.isValid() && 
               formData.end_time && formData.end_time.isValid() && 
               (() => {
                 const startDateTime = formData.date
                   .hour(formData.start_time.hour())
                   .minute(formData.start_time.minute())
                   .second(0);
                 const endDateTime = formData.date
                   .hour(formData.end_time.hour())
                   .minute(formData.end_time.minute())
                   .second(0);
                 return endDateTime.isBefore(startDateTime);
               })() && (
                <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, ml: 1.75 }}>
                  This will span to the next day
                </Typography>
              )}
            </Box>
            
            {/* Duration (Editable, auto-calculated from start and end time) */}
            <Box sx={{ mt: 2, mb: 2, px: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Typography sx={{ flexGrow: 1, pt: 1 }}>
                  Duration (HH:MM) (optional)
                </Typography>
                <TextField
                  type="text"
                  inputRef={durationInputRef}
                  value={durationInputValue !== null ? durationInputValue : minutesToHHMM(formData.duration_minutes)}
                  onChange={(e) => {
                    // Handle all input including deletion/backspace
                    const inputValue = e.target.value;
                    const input = e.target;
                    const cursorPosition = input.selectionStart;
                    
                    // Always update the display value to allow deletion
                    setDurationInputValue(inputValue);
                    durationPrevValueRef.current = inputValue;
                    
                    // Check if hours > 24
                    const hours = getHoursFromHHMM(inputValue);
                    if (inputValue && inputValue.includes(':') && hours > 24) {
                      setDurationError('Hours cannot exceed 24');
                      // Don't update formData if hours > 24, just keep the input value
                    } else {
                      // Check if total minutes > 1440 (24 hours)
                      if (inputValue && inputValue.includes(':')) {
                        const minutes = HHMMToMinutes(inputValue);
                        if (minutes > 1440) {
                          setDurationError('Duration cannot exceed 24 hours (1440 minutes)');
                          // Don't update formData if minutes > 1440, just keep the input value
                        } else {
                          setDurationError('');
                          // Update formData only if within valid range
                          const clampedValue = Math.min(Math.max(minutes, 0), 1440);
                          setFormData(prev => {
                            const updated = { ...prev, duration_minutes: clampedValue };
                            // If start_time exists, calculate end_time
                            if (updated.start_time && updated.start_time.isValid() && clampedValue > 0) {
                              const startDateTime = updated.date
                                .hour(updated.start_time.hour())
                                .minute(updated.start_time.minute())
                                .second(0);
                              updated.end_time = startDateTime.add(clampedValue, 'minute');
                            } else {
                              updated.end_time = null;
                            }
                            return updated;
                          });
                        }
                      } else {
                        setDurationError('');
                      }
                    }
                    
                    // Preserve cursor position after React updates
                    setTimeout(() => {
                      if (durationInputRef.current) {
                        // Use the exact cursor position from the input event
                        const newPosition = Math.min(cursorPosition, inputValue.length);
                        durationInputRef.current.setSelectionRange(newPosition, newPosition);
                      }
                    }, 0);
                  }}
                  onBlur={(e) => {
                    const inputValue = e.target.value.trim();
                    const hours = getHoursFromHHMM(inputValue);
                    
                    // Check if hours > 24 before processing
                    if (inputValue && inputValue.includes(':') && hours > 24) {
                      setDurationError('Hours cannot exceed 24');
                      // Don't normalize if there's an error, keep the input as-is
                      return;
                    }
                    
                    // Check if total minutes > 1440 before processing
                    if (inputValue && inputValue.includes(':')) {
                      const minutes = HHMMToMinutes(inputValue);
                      if (minutes > 1440) {
                        setDurationError('Duration cannot exceed 24 hours (1440 minutes)');
                        // Don't normalize if there's an error, keep the input as-is
                        return;
                      }
                    }
                    
                    setDurationError('');
                    
                    if (inputValue === '') {
                      // If empty on blur, default to 15 minutes (00:06)
                      const defaultMinutes = 15;
                      setDurationInputValue(null);
                      setFormData(prev => {
                        const updated = { ...prev, duration_minutes: defaultMinutes };
                        // If start_time exists, calculate end_time
                        if (updated.start_time && updated.start_time.isValid()) {
                          const startDateTime = updated.date
                            .hour(updated.start_time.hour())
                            .minute(updated.start_time.minute())
                            .second(0);
                          updated.end_time = startDateTime.add(defaultMinutes, 'minute');
                        }
                        return updated;
                      });
                    } else {
                      // Normalize on blur: convert any MM value to proper HH:MM
                      // e.g., 01:80 becomes 02:20 automatically
                      const minutes = HHMMToMinutes(inputValue);
                    const clampedValue = Math.min(Math.max(minutes, 0), 1440); // 24 hours max
                      const normalizedValue = minutesToHHMM(clampedValue);
                      setDurationInputValue(null);
                      handleDurationChange(normalizedValue);
                    }
                  }}
                  placeholder="HH:MM"
                  inputProps={{ 
                    pattern: '[0-9]{1,2}:[0-9]{1,}',
                    inputMode: 'numeric'
                  }}
                  size="small"
                  error={!!validationErrors.duration_minutes || !!durationError}
                  sx={{ width: 120 }}
                />
              </Box>
              {(durationError || validationErrors.duration_minutes) && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                  <Box>
                    {durationError && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {durationError}
                      </Typography>
                    )}
                    {validationErrors.duration_minutes && !durationError && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {validationErrors.duration_minutes}
                </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
            
            {/* Rate: DCC / PH, Admin and Developer, on an existing entry only */}
            {canEditRate && (
              <Box sx={{ mb: 2 }}>
                <TextField
                  label={initialData?.currency ? `Rate per hour (${initialData.currency})` : 'Rate per hour'}
                  value={rateInput}
                  onChange={(e) => setRateInput(e.target.value)}
                  fullWidth
                  inputProps={{ inputMode: 'numeric' }}
                  error={!!validationErrors.rate}
                  helperText={validationErrors.rate
                    || (isGroupEntry
                      ? 'Changes this member\'s entry only. The amount updates when you save.'
                      : 'Changes this entry only. The amount updates when you save.')}
                />
              </Box>
            )}

            {/* Notes Field */}
            <Box sx={{ mt: 1, mb: 1, px: 1 }}>
              <TextField
                label="Notes (optional)"
                name="notes"
                value={formData.notes || ''}
                onChange={handleChange}
                multiline
                minRows={3}
                fullWidth
                inputProps={{ maxLength: 100 }}
                helperText={`${(formData.notes || '').length}/100`}
                sx={{
                  '& .MuiInputBase-input': {
                    color: theme.palette.text.primary,
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary,
                  },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.background.paper,
                  },
                  '& fieldset': {
                    borderColor: theme.palette.info.main + '40',
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.info.main + '80',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.info.main,
                  },
                  '& .MuiFormHelperText-root': {
                    color: theme.palette.text.secondary,
                    textAlign: 'right',
                  },
                }}
              />
              {initialData && (
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>
                    Created by: <span style={{ fontWeight: 500 }}>{initialData?.created_by ? (memberLookup[String(initialData.created_by)] || `#${initialData.created_by}`) : '—'}</span>
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>
                    Last edited by: <span style={{ fontWeight: 500 }}>{initialData?.lastedited_by ? (memberLookup[String(initialData.lastedited_by)] || `#${initialData.lastedited_by}`) : '—'}</span>
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          {(() => {
            const primaryId = String(initialData?.member || '');
            const existingMemberIds = isGroupEntry ? groupEntries.map(e => String(e.member)) : [primaryId];
            const currentMemberIds = formData.member.map(String);
            const addedNames = currentMemberIds.filter(id => !existingMemberIds.includes(id)).map(id => combinedMemberLookup[id] || `Member #${id}`);
            const removedNames = isGroupEntry
              ? existingMemberIds.filter(id => !currentMemberIds.includes(id)).map(id => combinedMemberLookup[id] || `Member #${id}`)
              : [];
            if (addedNames.length === 0 && removedNames.length === 0) return null;
            return (
              <Box sx={{ px: 2, pb: 1, backgroundColor: theme.palette.background.default, textAlign: 'right' }}>
                {addedNames.length > 0 && (
                  <Typography variant="caption" sx={{ display: 'block', color: theme.palette.success.main }}>
                    {addedNames.length} time {addedNames.length === 1 ? 'entry' : 'entries'} will be created for{' '}
                    {addedNames.map((n, i) => <span key={i}><strong>{n}</strong>{i < addedNames.length - 1 ? ', ' : ''}</span>)}
                  </Typography>
                )}
                {removedNames.length > 0 && (
                  <Typography variant="caption" sx={{ display: 'block', color: theme.palette.warning.main }}>
                    {removedNames.length} time {removedNames.length === 1 ? 'entry' : 'entries'} will be removed for{' '}
                    {removedNames.map((n, i) => <span key={i}><strong>{n}</strong>{i < removedNames.length - 1 ? ', ' : ''}</span>)}
                  </Typography>
                )}
              </Box>
            );
          })()}
          <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
            <Button onClick={handleClose} variant="contained" sx={{ bgcolor: theme.palette.error.main, color: theme.palette.error.contrastText, '&:hover': { bgcolor: theme.palette.error.dark } }}>Cancel</Button>
            {isGroupEntry ? (
              <>
                <Button
                  onClick={() => { if (validateForm()) setGroupConfirmMode('myself'); }}
                  variant="contained"
                  sx={{ bgcolor: theme.palette.info.main, color: theme.palette.info.contrastText, '&:hover': { bgcolor: theme.palette.info.dark } }}
                >
                  Save for this one only
                </Button>
                <Button
                  onClick={() => { if (validateForm()) setGroupConfirmMode('all'); }}
                  variant="contained"
                  sx={{ bgcolor: theme.palette.success.main, color: theme.palette.success.contrastText, '&:hover': { bgcolor: theme.palette.success.dark } }}
                >
                  Save for all members
                </Button>
              </>
            ) : (
              <Button type='submit' variant="contained" sx={{ bgcolor: theme.palette.success.main, color: theme.palette.success.contrastText, '&:hover': { bgcolor: theme.palette.success.dark } }}>Save</Button>
            )}
          </DialogActions>
      </form>
    </Dialog>
    
    {/* Group scope confirmation dialog */}
    <Dialog open={!!groupConfirmMode} onClose={() => setGroupConfirmMode(null)} maxWidth="xs" fullWidth>
      {groupConfirmMode === 'myself' && (() => {
        const existingMemberIds = groupEntries.map(e => String(e.member));
        const currentMemberIds = formData.member.map(String);
        const otherMembers = groupEntries
          .filter(e => String(e.member) !== String(initialData?.member))
          .map(e => combinedMemberLookup[String(e.member)] || `Member #${e.member}`);
        const addedNames = currentMemberIds.filter(id => !existingMemberIds.includes(id)).map(id => combinedMemberLookup[id] || `Member #${id}`);
        const removedNames = existingMemberIds.filter(id => !currentMemberIds.includes(id)).map(id => combinedMemberLookup[id] || `Member #${id}`);
        const boldList = (names) => names.map((n, i) => <span key={i}><strong>{n}</strong>{i < names.length - 1 ? ', ' : ''}</span>);
        return (
          <>
            <DialogTitle sx={{ color: theme.palette.text.primary, backgroundColor: theme.palette.background.default }}>
              Confirm "Save for This One Only"
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: theme.palette.background.default }}>
              <Typography sx={{ color: theme.palette.text.primary }}>
                This will save changes only to this entry and remove it from the group.
                You will no longer be able to view or edit this entry alongside{' '}
                {otherMembers.length === 1
                  ? <strong>{otherMembers[0]}</strong>
                  : <>{otherMembers.slice(0, -1).map((n, i) => <span key={i}><strong>{n}</strong>, </span>)}<strong>{otherMembers[otherMembers.length - 1]}</strong></>
                }'s {otherMembers.length === 1 ? 'entry' : 'entries'}.
              </Typography>
              {addedNames.length > 0 && (
                <Typography sx={{ color: theme.palette.success.main, mt: 1 }}>
                  {addedNames.length} time {addedNames.length === 1 ? 'entry' : 'entries'} will be created for {boldList(addedNames)}
                </Typography>
              )}
              {removedNames.length > 0 && (
                <Typography sx={{ color: theme.palette.warning.main, mt: 1 }}>
                  {removedNames.length} time {removedNames.length === 1 ? 'entry' : 'entries'} will be removed for {boldList(removedNames)}
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
              <Button onClick={() => setGroupConfirmMode(null)} sx={{ color: theme.palette.text.primary }}>Cancel</Button>
              <Button
                onClick={() => { setGroupConfirmMode(null); performSubmit('myself'); }}
                variant="contained"
                sx={{ bgcolor: theme.palette.info.main, color: theme.palette.info.contrastText, '&:hover': { bgcolor: theme.palette.info.dark } }}
              >
                Save for This One Only
              </Button>
            </DialogActions>
          </>
        );
      })()}
      {groupConfirmMode === 'all' && (() => {
        const existingMemberIds = groupEntries.map(e => String(e.member));
        const currentMemberIds = formData.member.map(String);
        const retainedNames = currentMemberIds.filter(id => existingMemberIds.includes(id)).map(id => combinedMemberLookup[id] || `Member #${id}`);
        const addedNames = currentMemberIds.filter(id => !existingMemberIds.includes(id)).map(id => combinedMemberLookup[id] || `Member #${id}`);
        const removedNames = existingMemberIds.filter(id => !currentMemberIds.includes(id)).map(id => combinedMemberLookup[id] || `Member #${id}`);
        const boldList = (names) => names.map((n, i) => <span key={i}><strong>{n}</strong>{i < names.length - 1 ? ', ' : ''}</span>);
        return (
          <>
            <DialogTitle sx={{ color: theme.palette.text.primary, backgroundColor: theme.palette.background.default }}>
              Confirm "Save for All Members"
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: theme.palette.background.default }}>
              {retainedNames.length > 0 && (
                <Typography sx={{ color: theme.palette.text.primary, mb: 1 }}>
                  Changes will be applied to: {boldList(retainedNames)}
                </Typography>
              )}
              {addedNames.length > 0 && (
                <Typography sx={{ color: theme.palette.success.main, mt: 1 }}>
                  {addedNames.length} time {addedNames.length === 1 ? 'entry' : 'entries'} will be created for {boldList(addedNames)}
                </Typography>
              )}
              {removedNames.length > 0 && (
                <Typography sx={{ color: theme.palette.warning.main, mt: 1 }}>
                  {removedNames.length} time {removedNames.length === 1 ? 'entry' : 'entries'} will be removed for {boldList(removedNames)}
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
              <Button onClick={() => setGroupConfirmMode(null)} sx={{ color: theme.palette.text.primary }}>Cancel</Button>
              <Button
                onClick={() => { setGroupConfirmMode(null); performSubmit('all'); }}
                variant="contained"
                sx={{ bgcolor: theme.palette.success.main, color: theme.palette.success.contrastText, '&:hover': { bgcolor: theme.palette.success.dark } }}
              >
                Save for All Members
              </Button>
            </DialogActions>
          </>
        );
      })()}
    </Dialog>

    {/* Delete Confirmation Dialog */}
    {(() => {
      const isGroupDelete = isGroupEntry;
      return (
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle sx={{ color: theme.palette.text.primary, backgroundColor: theme.palette.background.default }}>
            Confirm "Delete Time Entry"
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: theme.palette.background.default }}>
            <Typography sx={{ color: theme.palette.text.primary }}>
              Are you sure you want to delete this time entry? This action cannot be undone.
            </Typography>
            {isGroupDelete && (
              <Typography variant="body2" sx={{ color: theme.palette.error.main, mt: 1 }}>
                This entry is part of a group with{' '}
                {groupEntries.map((e, i) => (
                  <span key={e.id}>
                    <strong>{combinedMemberLookup[String(e.member)] || `Member #${e.member}`}</strong>
                    {i < groupEntries.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </Typography>
            )}
            {initialData && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: theme.palette.action.hover, borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.text.primary }}>
                  Entry Details:
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                  <strong>Description:</strong> {initialData.description || 'No description'}
                </Typography>
                {initialData.start_time && (
                  <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                    <strong>Start Time:</strong> {new Date(initialData.start_time).toLocaleString(undefined, { timeZone: displayTimezone || 'UTC' })}
                  </Typography>
                )}
                {initialData.project && projectLookup[initialData.project] && (
                  <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                    <strong>Project:</strong> {projectLookup[initialData.project]}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
              variant="outlined"
              sx={{ borderColor: theme.palette.info.main, color: theme.palette.info.main, '&:hover': { bgcolor: theme.palette.action.hover } }}
            >
              Cancel
            </Button>
            {isGroupDelete ? (
              <>
                <Button
                  onClick={() => handleDeleteConfirm('single')}
                  disabled={deleting}
                  variant="contained"
                  sx={{ bgcolor: theme.palette.error.main, color: theme.palette.error.contrastText, '&:hover': { bgcolor: theme.palette.error.dark } }}
                >
                  {deleting ? 'Deleting...' : 'Delete this one only'}
                </Button>
                <Button
                  onClick={() => handleDeleteConfirm('all')}
                  disabled={deleting}
                  variant="contained"
                  sx={{ bgcolor: theme.palette.error.dark, color: theme.palette.error.contrastText, '&:hover': { bgcolor: theme.palette.error.dark } }}
                >
                  {deleting ? 'Deleting...' : `Delete ${groupEntries.length} entries for all members`}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => handleDeleteConfirm('single')}
                disabled={deleting}
                variant="contained"
                sx={{ bgcolor: theme.palette.error.main, color: theme.palette.error.contrastText, '&:hover': { bgcolor: theme.palette.error.dark } }}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      );
    })()}

    {/* Error dialog for API failures after optimistic update */}
    <Dialog open={apiErrorDialogOpen} onClose={() => setApiErrorDialogOpen(false)}>
      <DialogTitle sx={{ color: theme.palette.text.primary, backgroundColor: theme.palette.background.default }}>
        Error Updating Time Entry
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: theme.palette.background.default }}>
        <Typography sx={{ color: theme.palette.text.primary, mb: 2 }}>
          There was an issue with updating the time entry. The entry may have been updated in the table, but it may not have been saved correctly.
        </Typography>
        <Typography sx={{ color: theme.palette.text.primary }}>
          Please reload the page and try again.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
        <Button 
          onClick={() => window.location.reload()} 
          variant="contained" 
          sx={{ bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, '&:hover': { bgcolor: theme.palette.primary.dark } }}
        >
          Reload Page
        </Button>
        <Button 
          onClick={() => setApiErrorDialogOpen(false)} 
          variant="outlined"
          sx={{ color: theme.palette.text.primary, borderColor: theme.palette.info.main, '&:hover': { borderColor: theme.palette.info.dark } }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>

    {/* Duplicate check dialog */}
    <Dialog open={duplicateCheckDialogOpen} onClose={() => setDuplicateCheckDialogOpen(false)}>
      <DialogTitle sx={{ color: theme.palette.text.primary, backgroundColor: theme.palette.background.default }}>
        Duplicate Entry Detected
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: theme.palette.background.default }}>
        <Typography sx={{ color: theme.palette.text.primary }}>
          A time entry with the same member, time, and details already exists for this period.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
        <Button onClick={() => setDuplicateCheckDialogOpen(false)} sx={{ color: theme.palette.text.primary }}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            setDuplicateCheckDialogOpen(false);
            doEdit(duplicateCheckInfo.basePayload, duplicateCheckInfo.scope || 'myself');
          }}
          variant="contained"
          sx={{ bgcolor: theme.palette.success.main, color: theme.palette.success.contrastText, '&:hover': { bgcolor: theme.palette.success.dark } }}
        >
          Save Anyway
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default EditTimeEntryForm;
